import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import qs from 'qs';
import { axiosInstance } from '../../axios/axiosHandler';
import { useDebouncedCallback } from "use-debounce";
import { authHeader } from '../../store/auth-slice/auth-slice';

interface TableGridProps {
    baseURL: string,
    columns: GridColDef[],
    method?: "get" | "post",
    rowsPerPage?: number,
    tableHeight?: number,
    fixedHeader?: boolean,
    hideSearch?: boolean,
    title?: string,
    filters?: object,
    refresh?: number,
    sortColumns?: GridSortModel,
    rowHeight?: number,
    searchText?: string,
    searchDebounceInterval?: number,
    disableRowSelection?: boolean,
    pageSizeOptions?: number[],
    onRowClick?: any,
    onCellClick?: any,
    getCellClassName?: any,
    aditionalQueryParams?: any,
    columnHeaderHeight?: number,
    getRowClassName?: any
};

const TableGrid = ({ 
    baseURL,
    columns,
    method = "get",
    rowsPerPage,
    tableHeight = 400,
    fixedHeader,
    hideSearch,
    title,
    filters,
    refresh,
    sortColumns,
    rowHeight,
    searchText = "",
    searchDebounceInterval = 750,
    disableRowSelection = false,
    pageSizeOptions = [5, 10, 25, 50],
    onRowClick,
    onCellClick,
    getCellClassName,
    aditionalQueryParams,
    columnHeaderHeight,
    getRowClassName
} : TableGridProps) => 
{
    const [pageSize, setPageSize] = useState(rowsPerPage ? rowsPerPage : 25);
    const [page, setPage] = useState(1);
    const [sortModel, setSortModel] = useState(sortColumns);
    const [isLoading, setIsLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [searchVal, setSearchVal] = useState("");
    const [serverData, setServerData] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        setSearchVal(searchText);
    }, [searchText]);

    async function request() {
        if (method?.toLowerCase() !== "get" && method?.toLowerCase() !== "post") {
            throw new Error('\'method\' prop must be equal to "get" or "post"');
        }
    
        const queryObject = createQueryObject();
        const queryString = createQueryString();
    
        let response;
        var header = authHeader();
        if (method.toLowerCase() === "get") {
            response = axiosInstance.get(`${baseURL}?${queryString}`, header);
        } 
        else {
            response = axiosInstance.post(baseURL, queryObject, header);
        }
    
        return response;
    }

    async function getTableData() {
        setIsLoading(true);
        try {
            const response = await request();
            setIsLoading(false);
            setServerData(response.data.items);
            setPage(response.data.currentPage);
            setRowCount(response.data.totalCount);
        } 
        catch (error) {
            setIsLoading(false);
        }
    }

    const getTableDataDebounced = useDebouncedCallback(
        getTableData,
        searchDebounceInterval
    );

    const createQueryObject = () => ({
        ...filters,
        ...aditionalQueryParams,
        currentPage: page,
        pageSize: pageSize,
        searchText: searchVal,
        sortColumns: sortModel?.map((x) => ({
            name: x.field,
            sortDirection: x.sort,
        })),
    });
    
    const createQueryString = function () {
        const query = createQueryObject();

        const queryString = qs.stringify(query, {
            encodeValuesOnly: true,
            allowDots: true,
        });

        return queryString;
    };
    useEffect(() => {
        if (baseURL) {
            if (isSearching) {
                getTableDataDebounced();
                setIsSearching(false);
            } else {
                getTableData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        page,
        pageSize,
        searchVal,
        sortModel,
        filters,
        refresh,
        aditionalQueryParams
    ]);
    
    const handleChangeRowsPerPage = (event: any) => {
        const newPageSize = event.target.value;
        
        if (newPageSize * (page - 1) >= rowCount) {
            setPage(1);
        }
    
        setPageSize(newPageSize);
    };

    const handleChangePage = (_: any, newPage: number) => {
        setPage(!isLoading ? newPage + 1 : newPage);
    };

    const requestSearch = (e: string) => {
        const value = e;
        setIsSearching(value != null && value !== "");
        setSearchVal(value);
    };

    const handleSortModelChange = function (sortModel : any) {
        setSortModel(sortModel);
    };

    return(
        <>
            {title && (
                <Box
                    sx={{
                        mb: 0.5, 
                        fontSize: '1.1rem', 
                        fontWeight: 'bold', 
                    }}
                >
                    {title}
                </Box>
            )}
            <Box sx={{ height: tableHeight, width: '100%' }}>
                <DataGrid
                    loading={isLoading}
                    rows={serverData}
                    rowCount={rowCount}
                    columns={columns}
                    pagination={true}
                    paginationModel={{ page: page - 1, pageSize }}
                    onPaginationModelChange={(obj) => {
                        handleChangePage(obj, obj.page);
                        handleChangeRowsPerPage({ target: { value: obj.pageSize } });
                    }}
                    paginationMode={"server"}
                    sortingMode={"server"}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableRowSelectionOnClick={disableRowSelection}
                    pageSizeOptions={pageSizeOptions}
                    onRowClick={onRowClick}
                    onCellClick={onCellClick}
                    getCellClassName={getCellClassName}
                    rowHeight={rowHeight}
                    columnHeaderHeight={columnHeaderHeight}
                    getRowClassName={getRowClassName}
                />
            </Box>
        </>
    )
}

export default TableGrid;